// @ts-strict
// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Description
//
import type {
  LetterName,
  Accidental,
  NoteName,
  NoteProperties,
  ShortNoteName,
} from 'shared/definitions/musicTheory/notesAndAccidentals';
import {
  accidentalIntegers,
  isAccidental,
  isLetterName,
  midiNumbersBasic,
  noteLetterIntegers,
  noteLetterPitchClasses,
  letterNames,
  accidentals,
} from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import utils from '../utils';
import Clef from './clef';
import Interval from './interval';
import MusicObject from './musicObject';
import { toHTML } from '../uTheory.Font/font';
import type { MidiInfoObject, MidiMessage } from './soundBufferHolder';
import type KeySignature from './keySignature';
import type CustomKeySignature from './customKeySignature';

export interface AriaStringOptions {
  /* Wheter to label notes' letter names. Defaults true */
  letterName?: boolean;
  /* Whether to label notes' accidentals. Defaults true */
  accidental?: boolean;
  /* Whether to lable notes' octaves. Defaults true */
  octave?: boolean;
  /* Wheter to show the clef. Default true */
  clef?: boolean;
  /* Defaults true if more than one measure */
  measureNumber?: boolean;
  /** If present, overrides the settings of timeSignature.hidden; this is used in uTheory-angular */
  timeSignature?: boolean;
  /* Defaults true, when false "eighth G" instead of "eighth-note G" */
  theWordNote?: boolean;
  /* Defaults true if more than one voice on a staff */
  voiceNumber?: boolean;
  /* Defaults true */
  keySignature?: boolean | { accidentalsCount: boolean; accidentalsNames: boolean };
  /* Defaults true */
  rhythm?: boolean;
  /* Identify the selcted note(s). Defaults true */
  selected?: boolean;
  /* Include barlines, defaults true */
  barline?: boolean;
  /* Defaults false, requires optional clef param */
  lineOrSpace?: boolean;
  /* Whether to use British names for the rhythms, defaults false */
  british?: boolean;
  /* What word to use before a lyric, default "lyric" */
  lyricWord?: string;
}

/**
 * ## note.ts
 *
 * Representation of a musical note.
 *
 * Acceptable format is:
 * ```typescript
 *  new Note(`${letterName}${'bb'|'b'|''|'#'|'x'}${octaveNumber|''}`)
 * ```
 *
 * For example:
 * ```typescript
 *  new Note('Ebb3');
 *  new Note('D'); // ==> D4;
 * ```
 */
export default class Note extends MusicObject implements NoteProperties {
  public letter: LetterName;

  public accidental: Accidental;

  public _octave: number = 4;

  set octave(n: number) {
    if (typeof n !== 'number') throw Error(`Octave must be a number, not ${n}`);
    this._octave = n;
  }

  get octave() {
    return this._octave;
  }

  public color?: string;

  public staccato?: number;

  public velocity?: number;

  public additionalAriaText?: string;

  /** Set to true to force the note's accidental to always render  */
  public forceAccidental?: boolean = false;

  /** Set to true to force the note's accidental to render as cautionary */
  public cautionaryAccidental?: boolean = false;

  /**
   * Representation of a musical pitch. Initialize from string as:
   * ```typescript
   *  `${letterName}{'bb'|'b'|''|'#'|'x'}{octaveNumber?}`
   * ```
   *
   * Initialize from another Note results in a cloned Note, with no
   * performance parameters cloned.
   */
  constructor(noteOrNoteName: NoteName | Note);
  /**
   * Representation of a musical pitch. Initialized as:
   * ```typescript
   *  new Note({ letter: 'C', accidental: '#', octave: '3' }); ==> <Note C#3>
   * ```
   */
  constructor(noteParts: Partial<NoteProperties>);
  /**
   * Representation of a musical pitch. When initialized with no parameters,
   * returns C4 (middle C).
   */
  constructor();
  constructor(noteOrNoteNameOrProperties?: NoteName | Note | Partial<NoteProperties>) {
    super();
    const { letter, accidental, octave } = parseNoteName(noteOrNoteNameOrProperties);
    this.letter = letter;
    this.accidental = accidental;
    this.octave = octave;
  }

  /**
   *  setName(noteName)
   *  takes String $noteName and splits at slashes
   *
   *  Some valid possibilities:
   *
   *
   *            Human language variants:
   *              C#4
   *              Fbb3, etc...
   *
   *            If an octave is omitted, it's assumed to be 4 (from Middle C)   *
   * @param noteOrNoteName
   * @returns
   */
  setName(noteOrNoteName?: NoteName | Note): Note {
    const { letter, accidental, octave } = parseNoteName(noteOrNoteName);
    this.letter = letter;
    this.accidental = accidental;
    this.octave = octave;

    return this;
  }

  getName(): string {
    return `${this.letter}/${this.accidental}/${this.octave}`;
  }

  setAccidental(accidental: Accidental): Note {
    this.accidental = accidental;
    return this;
  }

  get isWhiteNote(): boolean {
    return !this.isBlackNote;
  }

  get isBlackNote(): boolean {
    const blackNoteMidis = [1, 3, 6, 8, 10];
    const normalizedMidi = this.getMidiNumber() % 12;
    return blackNoteMidis.includes(normalizedMidi);
  }

  getAccidentalInt(): number {
    const accidentalInt = accidentalIntegers[this.accidental];
    return accidentalInt;
  }

  /* Which indefinite article should this note take? */
  aOrAn(): string {
    return utils.aOrAn(this.letter);
  }

  toAriaString(
    { letterName = true, accidental = true, octave = true, lineOrSpace }: AriaStringOptions = {},
    clef?: Clef,
    keySignature?: KeySignature | CustomKeySignature
  ): string {
    if (lineOrSpace && !clef) {
      throw Error('A clef is required to get the line or space for a note.');
    }
    const outsideKey = !!keySignature && !keySignature.contains(this);
    let text = '';
    if (letterName && !lineOrSpace) {
      text += this.letter;
    }
    if (lineOrSpace && clef) {
      text += this.toLineOrSpaceString(clef);
    }
    if (lineOrSpace && accidental && (outsideKey || this.forceAccidental)) {
      text += '-';
      switch (this.accidental) {
        case '#':
          text += 'sharp';
          break;
        case 'b':
          text += 'flat';
          break;
        case 'bb':
          text += 'double-flat';
          break;
        case 'x':
          text += 'double-sharp';
          break;
        case '':
          text += 'natural';
          break;
      }
    }

    if (accidental && !lineOrSpace) {
      text += text && (this.accidental || this.forceAccidental || outsideKey) ? '-' : '';
      switch (this.accidental) {
        case '#':
          text += 'sharp';
          break;
        case 'b':
          text += 'flat';
          break;
        case 'bb':
          text += 'double-flat';
          break;
        case 'x':
          text += 'double-sharp';
          break;
        case '':
          if (this.forceAccidental || outsideKey) text += 'natural';
          break;
      }
    }
    if (octave && !lineOrSpace) {
      text += `${this.octave}`;
    }
    return text + (this.additionalAriaText || '');
  }

  toLineOrSpaceString(clef: Clef) {
    const line = clef.getLine(this);
    let text = '';
    // if it's a line:
    if (line % 1 === 0) {
      text = 'on the ';
      // if it's a leger line above the staff:
      if (line > 5) {
        const ledgerLine = line - 5;
        text += `${utils.ordinal(ledgerLine)} leger line above the staff`;
      } else if (line <= 0) {
        const ledgerLine = -(line - 1);
        text += `${utils.ordinal(ledgerLine)} leger line below the staff`;
      } else {
        text += `${utils.ordinal(line)} line`;
      }
    } else {
      // if it is a space:
      text = 'in the ';
      const space = line - 0.5;
      // if it's a leger line above the staff:
      if (space > 4) {
        const ledgerSpace = space - 4;
        text += `${utils.ordinal(ledgerSpace)} space above the staff`;
      } else if (space <= 0) {
        const ledgerSpace = -(space - 1);
        text += `${utils.ordinal(ledgerSpace)} space below the staff`;
      } else {
        text += `${utils.ordinal(space)} space`;
      }
    }
    return text;
  }

  toString(): NoteName;
  toString(includeProperties: true): string;
  toString(includeProperties: false): NoteName;
  toString(includeProperties = false): NoteName | string {
    const noteName = (this.letter + this.accidental + this.octave) as NoteName;
    if (!includeProperties) return noteName;

    const propStrings: string[] = [];
    if (this.color) {
      propStrings.push(`c${this.color}`);
    }
    if (this.staccato) {
      propStrings.push(`s${this.staccato}`);
    }
    if (typeof this.velocity === 'number') {
      propStrings.push(`v${this.velocity}`);
    }
    return noteName + propStrings.join(',');
  }

  toVexString(): string {
    return `${this.letter + this.toVexAccidental()}/${this.octave}`;
  }

  toVexAccidental(): 'bb' | 'b' | 'n' | '#' | '##' {
    if (this.accidental === 'x') return '##';
    if (this.accidental === '') return 'n';
    return this.accidental;
  }

  toFontAccidentalUnformatted(): string {
    const accidental = this.accidental;
    if (accidental === '') return 'n';
    if (accidental === 'bb') return 'v';
    return accidental;
  }

  /**
   * Returns noteName without octave. If accidentalFont is true, returns HTML formatted accidentals.
   * @param accidentalFont
   * @returns {"Cb"}
   */
  toShortString(): ShortNoteName;
  toShortString(accidentalFont: false): ShortNoteName;
  toShortString(accidentalFont: true): string;
  toShortString(accidentalFont: boolean): ShortNoteName | string;
  toShortString(accidentalFont = false): ShortNoteName | string {
    if (accidentalFont) {
      return this.toAccidentalFontShortString();
    }
    return this.letter + this.accidental;
  }

  toAccidentalFontShortStringUnformatted(): string {
    let accidental: string = this.accidental;
    if (accidental === 'bb') accidental = 'v';
    accidental = accidental === '' ? '' : this.toFontAccidentalUnformatted();
    return this.letter + accidental;
  }

  toAccidentalFontShortString(): string {
    let accidental: string = this.accidental;
    if (accidental === '' && this.forceAccidental) accidental = 'n';
    const accidentalString = toHTML('accidental', this.accidental, { ariaSpaceBefore: true });

    return `${this.letter}${accidentalString}`;
  }

  toAccidentalFontString(): string {
    return this.toAccidentalFontShortString() + this.octave;
  }

  toJSON(): string {
    const note = {
      letter: this.letter,
      accidental: this.accidental,
      octave: this.octave,
      color: this.color,
      staccato: this.staccato,
      velocity: this.velocity,
    };
    return JSON.stringify(note);
  }

  getPitchClass(): number {
    // First figure out the PC for the letter:
    const genericInt = noteLetterPitchClasses[this.letter];
    const accidentalInt = accidentalIntegers[this.accidental];
    return genericInt + accidentalInt;
  }

  /**
   * Get the frequency of a note in equal temperament.
   */
  getFrequency(): number {
    const distanceFromA4 = this.getMidiNumber() - new Note('A4').getMidiNumber();
    return 440 * 2 ** (distanceFromA4 / 12);
  }

  getMidiNumber(): number {
    // returns midi number for note
    return this.getPitchClass() + (12 * this.octave + 12);
  }

  getMidiMessages(midiInfo?: MidiInfoObject): MidiMessage[] {
    const { startBeat, stopBeat } = {
      startBeat: 0,
      stopBeat: 4,
      ...(midiInfo || {}),
    };

    const midiNumber = this.getMidiNumber();
    return [
      {
        midiNumber,
        startBeat,
        stopBeat,
      },
    ];
  }

  // GCR To-Do -- add to PHP
  clone(): Note {
    return new Note(this.toString());
  }

  // ====================== COMPARISON OPERATORS ===========================
  toLetterComparator(): number {
    const thisLetterInt = noteLetterIntegers[this.letter];
    return 10 * this.octave + thisLetterInt;
  }

  // Note: the == operator will also work, so long as all properties are identical.
  equals(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.getName() === that.getName();
  }

  equalsEnharmonically(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.getMidiNumber() === that.getMidiNumber();
  }

  equalsEnharmonicallyIgnoreOctave(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    const thisMidi = this.getMidiNumber();
    const thatMidi = that.getMidiNumber();
    return thisMidi % 12 === thatMidi % 12;
  }

  /**
   * Gets all of the other notes that are enharmonically equivalent to this
   * note, EXCLUDING this note.
   */
  getEnharmonicallyEquivalentNotes(): Note[] {
    const notes: Note[] = [];
    letterNames.forEach((letter) => {
      accidentals.forEach((accidental) => {
        [this.octave, this.octave - 1, this.octave + 1].forEach((octave) => {
          const note = new Note(`${letter}${accidental}${octave}` as NoteName);
          if (note.equalsEnharmonically(this) && !note.equals(this)) {
            notes.push(note);
          }
        });
      });
    });
    return notes;
  }

  equalsIgnoreOctave(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.letter === that.letter && this.accidental === that.accidental;
  }

  equalsIgnoreAccidental(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.letter === that.letter && this.octave === that.octave;
  }

  isLessThanIgnoreAccidental(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.toLetterComparator() < that.toLetterComparator();
  }

  isGreaterThan(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.getMidiNumber() > that.getMidiNumber();
  }

  isLessThan(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.getMidiNumber() < that.getMidiNumber();
  }

  isGreaterThanIgnoreAccidental(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.toLetterComparator() > that.toLetterComparator();
  }

  isGreaterThanOrEquals(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.equalsEnharmonically(that) || this.isGreaterThan(that);
  }

  isGreaterThanOrEqualsIgnoreAccidental(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.toLetterComparator() >= that.toLetterComparator();
  }

  isLessThanOrEquals(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.equalsEnharmonically(that) || this.isLessThan(that);
  }

  isLessThanOrEqualsIgnoreAccidental(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    return this.toLetterComparator() <= that.toLetterComparator();
  }

  genericIsLessThan(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    const thatClone = that.clone();
    thatClone.accidental = '';
    const thisClone = this.clone();
    thisClone.accidental = '';
    return thisClone.isLessThan(thatClone);
  }

  genericIsGreaterThan(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that);
    const thatClone = that.clone();
    thatClone.accidental = '';
    const thisClone = this.clone();
    thisClone.accidental = '';
    return thisClone.isGreaterThan(thatClone);
  }

  genericIsLessThanOrEquals(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that).clone();
    that.accidental = '';
    const thisClone = this.clone();
    thisClone.accidental = '';
    return thisClone.equals(that) || thisClone.isLessThan(that);
  }

  genericIsGreaterThanOrEquals(that: NoteName | Note): boolean {
    if (!isStringOrNote(that)) return false;
    that = Note.stringToNote(that).clone();
    that.accidental = '';
    const thisClone = this.clone();
    return thisClone.equals(that) || thisClone.isGreaterThan(that);
  }

  /**
   * Given an optional array of clefs (default treble & bass) what clefs does this note
   * display in correctly?
   * @param clefsArray = ['treble', 'bass']
   * @returns {Array.<clefNames>}
   */
  // Given a choice of clefs, which one(s) am I happy in?
  prefersClef(
    clefsArray: (Clef | ClefName)[] = ['treble', 'bass'],
    maxLedgerLines = 2
  ): ClefName[] {
    const clefs = clefsArray
      .map(Clef.fromStringOrClef)
      .filter(
        (clef) =>
          this.genericIsLessThanOrEquals(clef.getNoteAtLine(5 + maxLedgerLines)) &&
          this.genericIsGreaterThanOrEquals(clef.getNoteAtLine(1 - maxLedgerLines))
      );

    return clefs.map((clef) => clef.toString());
  }

  // ## INTERVAL METHODS
  //
  // Helper methods which integrate with Interval() objects
  //

  getIntervalTo(that: NoteName | Note): Interval | false {
    that = Note.stringToNote(that);
    // Calculate generic, simple/compound, octaves
    return Interval.getInterval(this, that);
  }

  getGenericIntervalTo(that: NoteName | Note): number {
    that = Note.stringToNote(that);
    // Calculate generic, doesn't simplify compound, intentionally.
    return Interval.getGenericInterval(this, that);
  }

  getNoteAt(interval: IntervalName | Interval) {
    return Interval.getNoteAt(this, interval);
  }

  transpose(interval: Interval | IntervalName) {
    const newNote = this.getNoteAt(interval);
    if (!newNote) {
      throw new Error(`Unable to transpose ${this} by ${interval}`);
    }
    this.setName(newNote);
  }

  getNoteAtGeneric(interval: IntervalName | number | Interval, keySignature?: KeySignature) {
    return Interval.getNoteAtGenericInterval(this, interval, keySignature);
  }

  static fromStringOrNote(stringOrNote: Note | string): Note {
    if (stringOrNote instanceof Note) return stringOrNote;
    // Take in performance information / musicFile stuff, too:
    // Split on : for performance information
    const [noteName, propsString] = stringOrNote.split(':');
    const note = new Note(noteName as NoteName);
    if (!propsString) return note;
    const properties = propsString.split(',');
    properties.forEach((prop) => {
      switch (prop[0]) {
        case 'v':
          note.velocity = Number(prop.slice(1));
          break;
        case 's':
          note.staccato = Number(prop.slice(1));
          break;
        case 'c':
          note.color = prop.slice(1);
          break;
        default:
          break;
      }
    });
    return note;
  }

  static stringToNote(n: NoteName | Note) {
    return Note.fromStringOrNote(n);
  }

  static fromJSON(noteProps: object): Note {
    const note = new Note();
    utils.merge(note, noteProps);
    return note;
  }

  static frequencyToMidi(hz: number): number {
    if (hz <= 0) {
      throw Error('Frequency must be greater than 0.');
    }
    // We use equal temperament because, well, it's easy.
    const a4Midi = 69;
    // 440 * 2 ** (midi - a4Midi / 12) === hz;
    return 12 * Math.log2(hz / 440) + a4Midi;
  }

  static midiToFrequency(midi: number): number {
    // We use equal temperament because, well, it's easy.
    const a4Midi = 69;
    const halfStepsFromA4 = midi - a4Midi;
    return 440 * 2 ** (halfStepsFromA4 / 12);
  }

  static fromMidiAllSpellings(midiNumber: number): Note[] {
    const note = Note.fromMidi(midiNumber);
    return [note, ...note.getEnharmonicallyEquivalentNotes()];
  }

  static fromMidi(midiNumber: number): Note {
    if (!utils.isNumeric(midiNumber)) throw new Error('Midi must be a number.');
    midiNumber = Number(midiNumber);
    const letter: string =
      midiNumbersBasic[(midiNumber % 12) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11];
    const octave: number = Math.floor(midiNumber / 12 - 1);
    return new Note(`${letter}${octave}` as NoteName);
  }

  /**
   * @param {Object} [options]
   * @param {string} [options.highest]
   * @param {string} [options.lowest]
   * @param {string[]} [options.accidentals]
   *
   * @returns Note
   */
  static randomNote(
    options: {
      highest?: NoteName | Note;
      lowest?: NoteName | Note;
      accidentals?: Accidental[];
    } = {}
  ): Note {
    // Setup defaults:
    const {
      highest: highestNoteString,
      lowest: lowestNoteString,
      accidentals: accidentalsToInclude,
    } = {
      highest: 'C6' as NoteName,
      lowest: 'C2' as NoteName,
      accidentals: ['#', 'b', ''],
      ...options,
    };
    const highestNote = Note.stringToNote(highestNoteString);
    const lowestNote = Note.stringToNote(lowestNoteString);

    // Accidentals must have at least one option:
    if (!accidentalsToInclude.length) {
      throw new Error('randomNote option accidentals must have at least one accidental.');
    }

    // Get distance between highest & lowest note:
    const range = lowestNote.getGenericIntervalTo(highestNote);

    // Get a random generic interval w/in this range from lowest note:
    const interval = Math.ceil(Math.random() * range);
    const note = lowestNote.getNoteAtGeneric(interval);

    const inRangeAccidentals = accidentalsToInclude
      .filter((acc) => {
        const clone = note.clone();
        if (!isAccidental(acc)) {
          throw new Error(`${acc} is not a valid accidental.`);
        }
        return (
          clone.setAccidental(acc).isGreaterThanOrEquals(lowestNote) &&
          clone.setAccidental(acc).isLessThanOrEquals(highestNote)
        );
      })
      .filter(isAccidental);

    // Apply an accidental at random from the acceptible list:
    note.accidental = inRangeAccidentals[Math.floor(Math.random() * inRangeAccidentals.length)]!;

    // return the note:
    return note;
  }
}

function isStringOrNote(arg: any) {
  if (arg instanceof Note) return true;
  if (typeof arg === 'string') return true;
  return false;
}

function parseNoteName(noteOrNoteName: NoteName | Note | Partial<NoteProperties> = 'C4'): {
  letter: LetterName;
  accidental: Accidental;
  octave: number;
} {
  if (noteOrNoteName instanceof Note) {
    return {
      letter: noteOrNoteName.letter,
      accidental: noteOrNoteName.accidental,
      octave: noteOrNoteName.octave,
    };
  }

  if (typeof noteOrNoteName === 'object') {
    return {
      letter: noteOrNoteName.letter || 'C',
      accidental: noteOrNoteName.accidental || '',
      octave: noteOrNoteName.octave || 4,
    };
  }

  if (!utils.isString(noteOrNoteName)) {
    throw new Error(
      `noteName must be a string, not ${utils.typeof(noteOrNoteName)}: ${noteOrNoteName}}`
    );
  }

  // Strip slashes, we don't really need them but will accept them if we
  // are getting a note from VexFlow, for instance.
  // @GCR: I don't believe anywhere in the codebase uses slashes like this,
  // so I've removed them from the definition of NoteName. This is here for
  // protection.
  const noteName = noteOrNoteName.replace(/\//g, '') as NoteName;

  const noteParts: {
    letter: LetterName;
    accidental: Accidental;
    octave: number;
  } = {
    letter: 'C',
    accidental: '',
    octave: 4,
  };

  // First part must be a valid letter name
  // if it's lowercase (a la VexFlow) we'll uppercase it
  const letterName = noteName[0]?.toUpperCase();
  if (isLetterName(letterName)) {
    noteParts.letter = letterName;
  } else {
    throw new Error(
      `Invalid note name: The first character must be A-G, got noteName = "${noteName}"`
    );
  }

  // Second part should be accidental or numeric
  const accidental = noteName.match(/[#bbx]+/)?.[0];
  if (accidental && isAccidental(accidental)) {
    noteParts.accidental = accidental;
  } else if (accidental && !isAccidental(accidental)) {
    throw new Error(
      'Invalid accidental: Second part of note name must be an accidental x # empty b or bb'
    );
  }

  // If last digit is not a number, add octave
  const octave = noteName.match(/[0-9]+/)?.[0];
  // Note: we use isNumeric to allow strings
  if (utils.isNumeric(octave)) {
    noteParts.octave = Number(octave);
  } else if (octave) {
    throw new Error(`${octave} is not a valid octave number.`);
  }

  return noteParts;
}
